import { TripAttributes } from "@albatrosdeveloper/ave-models-package";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IPaginationDate, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { AbstractService } from "app/services/abstract.service";
import { environment as evn } from "environments/environment.prod";

import { Observable } from "rxjs";
import { ICreateTrip } from "./interfaces/createTrip";
import { IUpdateTrip } from "./interfaces/updateTrip";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = evn.API_AVE_TRIP_URL;
const apiUrlExport = evn.API_AVE_DOCUMENT_EXPORT_URL;
const moduleName = "trip";

@Injectable({
  providedIn: "root",
})
export class TripRouteService extends AbstractService<
  TripAttributes,
  Partial<TripAttributes>,
  Partial<TripAttributes>
> {
  currentFilter: CombinedFilter<TripAttributes>;
  currentFilterData: any;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  withCountTrip(
    options: IPaginationDate<TripAttributes>,
    dataFilter: IFilters
  ): Observable<IPaginationResult<TripAttributes>> {
    const hasSearch = !!options.search;

    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<TripAttributes>[] => {
      const filterFn = params.useOrWhere ? orWhere : andWhere;

      return [
        filterFn("_deleted", Ops.eq(false)),
        // 3 filtros de fechas
        dataFilter && dataFilter.end_startTrip && dataFilter.end_endTrip
          ? andWhere("dateHourEndTeoric", Ops.btwnDates(dataFilter.end_startTrip, dataFilter.end_endTrip))
          : noop(),

        dataFilter && dataFilter.startTrip && dataFilter.endTrip
          ? andWhere("dateHourStartTeoric", Ops.btwnDates(dataFilter.startTrip, dataFilter.endTrip))
          : noop(),

        dataFilter && dataFilter.charge_start && dataFilter.charge_end
          ? andWhere("loadDateTime", Ops.btwnDates(dataFilter.charge_start, dataFilter.charge_end))
          : noop(),

        // 2 filtros únicos
        dataFilter && dataFilter.companyId
          ? andWhere("company.id" as keyof TripAttributes, Ops.eq(dataFilter.companyId))
          : noop(),

        dataFilter && dataFilter.routeTypeId
          ? andWhere("routes.id" as keyof TripAttributes, Ops.eq(dataFilter.routeTypeId))
          : noop(),

        // 6 filtros de arrays
        dataFilter.ordersToFilter && dataFilter.ordersToFilter.length > 0
          ? andWhere("id", Ops.in(...dataFilter.ordersToFilter.map(id => Number(id))))
          : noop(),

        dataFilter.subRouteId && dataFilter.subRouteId.length > 0
          ? andWhere("subRoute.id" as keyof TripAttributes, Ops.in(...dataFilter.subRouteId))
          : noop(),

        dataFilter.salesOfficeId && dataFilter.salesOfficeId.length > 0
          ? andWhere("salesOffice.id" as keyof TripAttributes, Ops.in(...dataFilter.salesOfficeId))
          : noop(),

        dataFilter.masterUserId && dataFilter.masterUserId.length > 0
          ? andWhere("user.business_partner_id" as keyof TripAttributes, Ops.in(...dataFilter.masterUserId))
          : noop(),

        dataFilter.functionNames && dataFilter.functionNames.length > 0
          ? andWhere("subRoute.function.name" as keyof TripAttributes, Ops.in(...dataFilter.functionNames))
          : noop(),

        dataFilter.tripStatuses && dataFilter.tripStatuses.length > 0
          ? andWhere("statusTripId" as keyof TripAttributes, Ops.in(...dataFilter.tripStatuses))
          : noop(),
      ];
    };

    // Objeto 1: Búsqueda por nombre de ruta
    const searchBySubRouteNameQueryObject: FilterFn<TripAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch
        ? andWhere("subRoute.name" as keyof TripAttributes, Ops.like(options.search!, "i"))
        : noop(),
    ];

    // Objeto 2: Búsqueda por username (solo si hay término de búsqueda)
    const searchByUsernameQueryObject: FilterFn<TripAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere("user.user_login.username" as keyof TripAttributes, Ops.like(options.search!, "i")),

        ]
      : [];

    // Objeto 3: Búsqueda por código externo (solo si hay término de búsqueda)
    const searchByCodeQueryObject: FilterFn<TripAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere("subRoute.externalKeys.code" as keyof TripAttributes, Ops.like(options.search!, "i")),
        ]
      : [];

    // Objeto 4: Búsqueda por ID numérico (solo si es número válido)
    const searchNumber = Number(options.search);
    const isValidNumber = hasSearch && !isNaN(searchNumber) && searchNumber > 0;
    const searchByIdQueryObject: FilterFn<TripAttributes>[] = isValidNumber
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere("id", Ops.eq(searchNumber)),
        ]
      : [];

    const query = buildQuery<TripAttributes>(
      ...searchBySubRouteNameQueryObject,
      ...searchByUsernameQueryObject,
      ...searchByCodeQueryObject,
      ...searchByIdQueryObject,
      orderBy(
        options.sort ?? ("dateHourStartTeoric" as keyof TripAttributes),
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<TripAttributes>>(API_URL);
  }

  /**
 * Obtiene todos los viajes para vista árbol (sin paginación)
 * @param dataToFilter Filtros aplicados
 * @returns Todos los registros para construir el árbol
 */
  findAllForTreeView(
    dataToFilter: IFilters
  ): Observable<IPaginationResult<TripAttributes>> {
    const query = buildQuery<TripAttributes>(
      // No hay búsqueda activa - solo filtros base
      where("subRoute.name" as keyof TripAttributes, Ops.eq("")),

      // 3 filtros de fechas
      dataToFilter && dataToFilter.startTrip && dataToFilter.endTrip
        ? andWhere("dateHourStartTeoric", Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip))
        : noop(),

      dataToFilter && dataToFilter.end_startTrip && dataToFilter.end_endTrip
        ? andWhere("dateHourEndTeoric", Ops.btwnDates(dataToFilter.end_startTrip, dataToFilter.end_endTrip))
        : noop(),

      dataToFilter && dataToFilter.charge_start && dataToFilter.charge_end
        ? andWhere("loadDateTime", Ops.btwnDates(dataToFilter.charge_start, dataToFilter.charge_end))
        : noop(),

      // 2 filtros únicos
      dataToFilter && dataToFilter.companyId
        ? andWhere("company.id" as keyof TripAttributes, Ops.eq(dataToFilter.companyId))
        : noop(),

      dataToFilter && dataToFilter.routeTypeId
        ? andWhere("routes.id" as keyof TripAttributes, Ops.eq(dataToFilter.routeTypeId))
        : noop(),

      // 6 filtros de arrays
      dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
        ? andWhere("subRoute.id" as keyof TripAttributes, Ops.in(...dataToFilter.subRouteId))
        : noop(),

      dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0
        ? andWhere("salesOffice.id" as keyof TripAttributes, Ops.in(...dataToFilter.salesOfficeId))
        : noop(),

      dataToFilter.masterUserId && dataToFilter.masterUserId.length > 0
        ? andWhere("user.business_partner_id" as keyof TripAttributes, Ops.in(...dataToFilter.masterUserId))
        : noop(),

      dataToFilter.functionCodes && dataToFilter.functionCodes.length > 0
        ? andWhere("subRoute.function.code" as keyof TripAttributes, Ops.in(...dataToFilter.functionCodes))
        : noop(),

      dataToFilter.functionNames && dataToFilter.functionNames.length > 0
        ? andWhere("subRoute.function.name" as keyof TripAttributes, Ops.in(...dataToFilter.functionNames))
        : noop(),

      dataToFilter.tripStatuses && dataToFilter.tripStatuses.length > 0
        ? andWhere("statusTripId" as keyof TripAttributes, Ops.in(...dataToFilter.tripStatuses))
        : noop(),

      orderBy(
        "statusTripId" as keyof TripAttributes,
        OrderVariant.Asc
      )
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCountByTripv2`;
    return this.http.post<IPaginationResult<TripAttributes>>(API_URL, {
      filter: query
    });
  }

  createTrip(data: any) {
    const API_URL: string = apiUrl + `api/${moduleName}`;
    return this.http.post<IPaginationResult<TripAttributes>>(API_URL, data);
  }

  updateTrip(data: IUpdateTrip, id: string) {
    const API_URL: string = apiUrl + `api/${moduleName}/${id}`;
    return this.http.patch<IPaginationResult<TripAttributes>>(API_URL, data);
  }

  /**
    * Find to combo box
    * @returns Data
    */
  findToComboBox(tripId?): Observable<IRequestResult<TripAttributes[]>> {
    const query = buildQuery<TripAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      tripId ? andAllWhere("id" as keyof TripAttributes, Ops.eq(tripId)) : noop(),
      select(["vehicle"])
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<TripAttributes[]>>(API_URL);
  }

  downloadResumesPDF(data): Observable<any> {
    const API_URL: string = apiUrlExport + `api/trip-resume-pdf?format=letter&landscape=false&scale=1&printBackground=true&marginTop=10mm&marginBottom=10mm&marginLeft=10mm&marginRight=10mm`;
    return this.http.post(API_URL, data, { responseType: 'blob' });
  }

  downloadResumesExcel(data): Observable<any> {
    const API_URL: string = apiUrlExport + `api/trip-excel?separateSheets=false`;
    return this.http.post(API_URL, data, { responseType: 'blob' });
  }
}
